import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams, Navigate } from 'react-router-dom';
import VenueDashboard from '../VenueDashboard/VenueDashboard';
import {
  forceVenueDashboardRelogin,
  requiresVenueDashboardRelogin,
  touchVenueDashboardActivity
} from '../../utils/venueDashboardSession';
import { usePageSeo } from '../Seo/PageSeo';
import './VenueDashboardWrapper.css';

const ERROR_NOT_LOGGED_IN = 'not_signed_in';
const ERROR_NOT_ONBOARDED = 'not_onboarded';
const ERROR_NO_VENUE = 'no_venue';
const ERROR_NO_PERMISSION = 'wrong_venue';
const ERROR_GENERIC = 'generic';

const VenueDashboardWrapper = () => {
  usePageSeo({
    title: 'Venue dashboard',
    path: '/venue-dashboard',
    noindex: true
  });

  const { currentUser, userProfile, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [venueId, setVenueId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorType, setErrorType] = useState(null);

  useEffect(() => {
    const initializeVenueDashboard = () => {
      try {
        setLoading(true);
        setErrorType(null);

        if (requiresVenueDashboardRelogin()) {
          forceVenueDashboardRelogin(
            `${window.location.pathname}${window.location.search}`
          );
          return;
        }

        touchVenueDashboardActivity();

        const urlVenueId = searchParams.get('venueId');
        let selectedVenueId = null;

        if (userProfile.is_admin) {
          // Admins can access any venue via URL param
          if (urlVenueId) {
            selectedVenueId = urlVenueId;
          } else if (userProfile.owned_venues?.length > 0) {
            selectedVenueId = userProfile.owned_venues[0].id;
          } else {
            setErrorType(ERROR_NO_VENUE);
            setLoading(false);
            return;
          }
        } else if (userProfile.user_type === 'venue_owner') {
          if (urlVenueId) {
            const ownsVenue = userProfile.owned_venues?.some(v => v.id === urlVenueId)
              || userProfile.venue_id === urlVenueId;

            if (!ownsVenue) {
              setErrorType(ERROR_NO_PERMISSION);
              setLoading(false);
              return;
            }
            selectedVenueId = urlVenueId;
          } else {
            selectedVenueId = userProfile.owned_venues?.[0]?.id || userProfile.venue_id;
            if (!selectedVenueId) {
              setErrorType(ERROR_NO_VENUE);
              setLoading(false);
              return;
            }
          }
        } else {
          setErrorType(ERROR_NOT_ONBOARDED);
          setLoading(false);
          return;
        }

        setVenueId(selectedVenueId);
      } catch {
        setErrorType(ERROR_GENERIC);
      } finally {
        setLoading(false);
      }
    };

    if (authLoading) return;

    if (!localStorage.getItem('access_token') || !currentUser) {
      setLoading(false);
      setErrorType(ERROR_NOT_LOGGED_IN);
      return;
    }

    if (!userProfile) return;

    initializeVenueDashboard();
  }, [currentUser, userProfile, authLoading, searchParams]);

  if (loading) {
    return (
      <div className="venue-dashboard-wrapper">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading venue dashboard...</p>
        </div>
      </div>
    );
  }

  if (errorType) {
    const params = new URLSearchParams();
    const urlVenueId = searchParams.get('venueId');
    if (urlVenueId) params.set('venueId', urlVenueId);
    params.set('error', errorType);
    return <Navigate to={`/dashboard-login?${params.toString()}`} replace />;
  }

  if (venueId) {
    return (
      <div className="venue-dashboard-wrapper">
        <VenueDashboard venueId={venueId} />
      </div>
    );
  }

  return null;
};

export default VenueDashboardWrapper;
